import type { MaterialItemsSsrmRow } from "~produce/production/materialItems/helpers/materialItemsSsrmRow";

import { type GridRequestInput } from "~/gql/graphqlBase.generated";

export const PRICING_STATUS_COL_ID = "pricingStatus";
export const PRICING_REASON_COL_ID = "pricingReason";
export const NEEDS_PRICING_COL_ID = "needsPricing";
export const PRICING_VENDOR_SCOPE_COL_ID = "pricingVendorScope";
export const ROYALTY_COVERAGE_COL_ID = "royaltyCoverage";

export type MaterialPricingStatus =
	| "FUTURE_ONLY"
	| "NO_APPLICABLE_PRICE"
	| "NO_DEFAULT_VENDOR"
	| "NO_VENDOR"
	| "READY"
	| "UNAVAILABLE"
	| "VENDOR_INACTIVE";

export type MaterialPricingReason =
	| "INVALID_LEGACY_DATING"
	| "NO_DEFAULT_VENDOR"
	| "NO_PRICE_RECORDED"
	| "NO_VENDOR_ASSIGNED"
	| "NOT_YET_EFFECTIVE"
	| "ONLY_VOIDED_OR_DETACHED_PRICES"
	| "PRICE_IDENTITY_NOT_READY"
	| "SELECTED_VENDOR_NOT_ASSIGNED"
	| "UNREADABLE_STORED_PRICE"
	| "VENDOR_INACTIVE";

export type SupplierRoyaltyStatus = "CONFIRMED_NONE" | "LEGACY_UNSPECIFIED" | "REVIEWED_UNCONFIRMED" | "SPECIFIED";

export type SupplierRoyaltyCoverage = "CONFIRMED" | "INCOMPLETE" | "NOT_APPLICABLE";

export interface MaterialPricingWorklistScope {
	needsPricingOnly: boolean;
	vendorId: string | null;
	vendorName: string | null;
}

export const DEFAULT_PRICING_WORKLIST_SCOPE: MaterialPricingWorklistScope = {
	needsPricingOnly: false,
	vendorId: null,
	vendorName: null
};

export const withPricingWorklistScope = (
	request: GridRequestInput,
	scope: MaterialPricingWorklistScope
): GridRequestInput => {
	if (!scope.needsPricingOnly) return request;
	const filterModel = (request.filterModel as Record<string, unknown> | null) ?? {};
	return {
		...request,
		filterModel: {
			...filterModel,
			[NEEDS_PRICING_COL_ID]: { filterType: "set", values: ["true"] },
			...(scope.vendorId ? { [PRICING_VENDOR_SCOPE_COL_ID]: { filterType: "set", values: [scope.vendorId] } } : {})
		}
	};
};

export type MaterialPricingAvailability = "AVAILABLE" | "UNAVAILABLE" | "UNKNOWN";

export const resolvePricingAvailability = (rows: MaterialItemsSsrmRow[]): MaterialPricingAvailability => {
	if (rows.length === 0) return "UNKNOWN";
	return rows.every((row) => row.pricingStatus === "UNAVAILABLE") ? "UNAVAILABLE" : "AVAILABLE";
};

export const isRoyaltyKnown = (status: SupplierRoyaltyStatus | null): boolean =>
	status === "CONFIRMED_NONE" || status === "SPECIFIED";

export const hasSelectedQuote = (status: MaterialPricingStatus | null): boolean =>
	status === "READY" || status === "VENDOR_INACTIVE";

export interface KnownCostSubtotal {
	base: number;
	isComplete: boolean;
	royalty: number | null;
	subtotal: number;
}

export const resolveKnownCostSubtotal = (row: MaterialItemsSsrmRow): KnownCostSubtotal | null => {
	if (!hasSelectedQuote(row.pricingStatus) || row.priceUnitCost === null) return null;
	const royaltyKnown = isRoyaltyKnown(row.supplierRoyaltyStatus) && row.supplierRoyaltyPerEach !== null;
	const royalty = royaltyKnown ? row.supplierRoyaltyPerEach : null;
	return {
		base: row.priceUnitCost,
		isComplete: row.royaltyCoverage === "CONFIRMED",
		royalty,
		subtotal: row.priceUnitCost + (royalty ?? 0)
	};
};

export const hasRoyaltyCoverageWarning = (row: MaterialItemsSsrmRow): boolean =>
	hasSelectedQuote(row.pricingStatus) && row.royaltyCoverage === "INCOMPLETE";
